import { Wrap } from './styles';

import { convertDMS } from 'services/utils/converters';

import Icon from '@map/components/Icons';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import withStyles from '@material-ui/styles/withStyles';
/**
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 */
import { useEffect, useState } from 'react';
/**
 *  useMap é um hook personalizado criado para gerenciar a comunicação
 *  dos componentes com o mapa.
 */
import { useMap } from 'views/MapView/hooks';

const StyledMenuItem = withStyles({
  root: {
    fontSize: '14px',
    '& svg': {
      position: 'absolute',
      left: '10px',
    },
  },
  gutters: {
    padding: '6px 25px 6px 28px',
  },
})(MenuItem);

const MapCoordinate = ({ ...props }) => {
  const [format, setFormat] = useState({
    dms: {
      label: 'Grau/Minuto/Segundo',
      firstCoordLabel: 'Lat',
      secondCoordLabel: 'Long',
      defaultValue: ['00° 00\' 00"', '00° 00\' 00"'],
    },
    dd: {
      label: 'Grau Decimal',
      firstCoordLabel: 'Lat',
      secondCoordLabel: 'Long',
      defaultValue: ['00.00000°', '00.00000°'],
    },
    utm: {
      label: 'UTM',
      firstCoordLabel: 'E',
      secondCoordLabel: 'N',
      defaultValue: ['000000.00m', '0000000.00m', '24L'],
      enableAtZoom: 13,
      disabled: true,
    },
  });

  const [currentFormat, setCurrentFormat] = useState(format.dms);
  const [latlng, setLatlng] = useState(null);
  const [coordinates, setCoordinates] = useState(currentFormat.defaultValue);
  const [anchorEl, setAnchorEl] = useState(null);

  const { map } = useMap();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (newFormat) => (event) => {
    setCurrentFormat(newFormat);
    handleClose();
  };

  useEffect(() => {
    const updateLatLng = (e) => setLatlng(e.latlng);
    const resizeCheck = (resizeEvent) => {
      let addEvent, removeEvent;
      if (
        (resizeEvent && resizeEvent.newSize.x > 768) ||
        window.innerWidth > 768
      ) {
        addEvent = 'mousemove';
        removeEvent = 'mousedown';
      } else {
        addEvent = 'mousedown';
        removeEvent = 'mousemove';
      }
      map.on(addEvent, updateLatLng);
      map.off(removeEvent, updateLatLng);
    };
    map.on('resize', resizeCheck);
    resizeCheck();
  });

  useEffect(() => {
    if (latlng) {
      if (currentFormat === format.dms) {
        let newCoordinates = convertDMS(latlng.wrap().lat, latlng.wrap().lng);
        setCoordinates([newCoordinates.lat, newCoordinates.lng]);
      } else if (currentFormat === format.dd) {
        let latSymbol = latlng.lat > 0 ? '' : '-';
        let newLat = latlng.wrap().lat.toString().split('.');
        newLat = `${latSymbol}${newLat[0]
          .replace(latSymbol, '')
          .padStart(2, '0')}.${
          newLat[1] ? newLat[1].substr(0, 5).padEnd(5, '0') : '00000'
        }°`;
        let lngSymbol = latlng.lng > 0 ? '' : '-';
        let newLng = latlng.wrap().lng.toString().split('.');
        newLng = `${lngSymbol}${newLng[0]
          .replace(lngSymbol, '')
          .padStart(2, '0')}.${
          newLng[1] ? newLng[1].substr(0, 5).padEnd(5, '0') : '00000'
        }°`;
        setCoordinates([newLat, newLng]);
      } else if (currentFormat === format.utm) {
        let utm = latlng.utm();
        let e = `${Number.parseFloat(utm.x).toFixed(2)}m`;
        let n = `${Number.parseFloat(utm.y).toFixed(2)}m`;
        let zoneBand = `${utm.zone}${utm.band}`;
        setCoordinates([e, n, zoneBand]);
      }
    }
  }, [latlng, currentFormat, format]);

  useEffect(() => {
    let newFormat = format;
    Object.keys(newFormat).forEach((k) => {
      if (newFormat[k].enableAtZoom) {
        // eslint-disable-next-line no-underscore-dangle
        if (map._zoom >= newFormat[k].enableAtZoom) {
          newFormat[k].disabled = false;
        } else {
          newFormat[k].disabled = true;
          if (currentFormat === newFormat[k]) setCurrentFormat(format.dms);
        }
      }
    });
    if (format !== newFormat) setFormat(newFormat);
    // eslint-disable-next-line no-underscore-dangle
  }, [map._zoom, format, currentFormat]);

  return (
    <div>
      <Wrap onClick={handleClick}>
        {currentFormat === format.utm && (
          <div className="field frist-field">
            <label>{coordinates[2]}</label>
          </div>
        )}
        <div
          className={`field ${currentFormat !== format.utm && 'frist-field'}`}
        >
          <label>{currentFormat.firstCoordLabel}: </label>
          <span>{coordinates[0]}</span>
        </div>
        <div className="field last-field">
          <label>{currentFormat.secondCoordLabel}: </label>
          <span>{coordinates[1]}</span>
        </div>
      </Wrap>
      <Menu
        id="map-coordinate-format-select"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        {Object.keys(format).map((v, i) => (
          <StyledMenuItem
            onClick={handleChange(format[v])}
            disabled={format[v].disabled}
            key={i}
          >
            {currentFormat === format[v] && <Icon i="map-check" />}
            {format[v].label}
          </StyledMenuItem>
        ))}
      </Menu>
    </div>
  );
};

MapCoordinate.properties = {
  id: 'CPN-MA_MAP-COORDINATE',
  alias: 'Localizador de Coordenadas',
  description: '...',
};

export default MapCoordinate;
